import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Option } from '@interfaces/common/option';
import { FilteredDataResultDto } from '@interfaces/common/filtered-data-result-dto.interface';
import { CorporateCharterCompaniesRequest } from '@interfaces/corporate-charter/requests/corporate-charter.request';
import {
  CorporateCharterCompanyListItemDto,
  CorporateCharterCompanyStatus,
} from '@interfaces/corporate-charter/responses/corporate-charter.response';
import { CorporateCharterHttpService } from '@services/http/corporate-charter/corporate-charter-http.service';
import { SharedModule } from '@shared/shared.module';
import { AppState } from '@src/app/state';
import { selectCompanyId } from '@src/app/state/auth/auth.selectors';
import { CharterCompanySearchService } from '@features/dashboard/company/corporate-charter/services/charter-company-search.service';
import { CharterCompaniesTableComponent } from '@features/dashboard/company/corporate-charter/components/charter-companies-table/charter-companies-table.component';
import {
  CHARTER_BREADCRUMB_HOME,
  CHARTER_LIST_BREADCRUMBS,
  corporateCharterRoute,
} from '@features/dashboard/company/corporate-charter/corporate-charter.util';
import { Subject, catchError, combineLatest, of, switchMap, take, tap } from 'rxjs';

type StatusFilter = CorporateCharterCompanyStatus | 'all';

/**
 * Lista "Societário" (`/dashboard/company/societario`, menu Gerenciamento > Empresas > Societário): as empresas do
 * escopo com a situação do contrato social (vigente, lendo, com pendência ou sem contrato no Data Room). Clicar na
 * linha abre a tela da empresa. Usuário de empresa (papel COMPANY) com uma empresa só não vê a lista: vai direto
 * para a própria empresa.
 */
@Component({
  selector: 'app-corporate-charter-companies-page',
  imports: [SharedModule, FormsModule, CharterCompaniesTableComponent],
  templateUrl: './corporate-charter-companies-page.component.html',
  styleUrl: './corporate-charter-companies-page.component.scss',
  providers: [CharterCompanySearchService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CorporateCharterCompaniesPageComponent {
  private readonly http = inject(CorporateCharterHttpService);
  private readonly router = inject(Router);
  private readonly store = inject<Store<AppState>>(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly access = inject(CharterCompanySearchService);

  protected readonly breadcrumbHome = CHARTER_BREADCRUMB_HOME;
  protected readonly breadcrumbs = CHARTER_LIST_BREADCRUMBS;
  protected readonly pageSize = 25;
  protected readonly statusOptions: Option<StatusFilter>[] = [
    { label: 'Todas as situações', value: 'all' },
    { label: 'Vigente', value: 'current' },
    { label: 'Lendo', value: 'reading' },
    { label: 'Com pendência', value: 'review' },
    { label: 'Contrato no Data Room, não lido', value: 'pending' },
    { label: 'Sem contrato no Data Room', value: 'none' },
  ];

  protected readonly search = signal('');
  protected readonly status = signal<StatusFilter>('all');
  protected readonly page = signal(1);
  protected readonly loading = signal(true);
  protected readonly loadFailed = signal(false);
  protected readonly result =
    signal<FilteredDataResultDto<CorporateCharterCompanyListItemDto> | null>(null);

  protected readonly items = computed(() => this.result()?.data ?? []);
  protected readonly total = computed(() => this.result()?.total ?? 0);
  protected readonly pages = computed(() => Math.max(1, this.result()?.pages ?? 1));
  protected readonly hasFilter = computed(() => !!this.search().trim() || this.status() !== 'all');
  protected readonly rangeLabel = computed(() => {
    const total = this.total();
    if (!total) {
      return '';
    }
    const first = (this.page() - 1) * this.pageSize + 1;
    const last = Math.min(this.page() * this.pageSize, total);
    return `${first}–${last} de ${total}`;
  });

  private readonly reload$ = new Subject<void>();

  constructor() {
    this.reload$
      .pipe(
        tap(() => {
          this.loading.set(true);
          this.loadFailed.set(false);
        }),
        switchMap(() =>
          this.http.listCompanies(this.buildRequest()).pipe(
            catchError(() => {
              this.loadFailed.set(true);
              return of(null);
            })
          )
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(result => {
        this.result.set(result);
        this.loading.set(false);
      });

    // Usuário de empresa com uma empresa só não tem lista: vai direto para a própria. Com várias, vê a lista.
    combineLatest([this.access.canSwitch(), this.store.select(selectCompanyId).pipe(take(1))])
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(([canSwitch, companyId]) => {
        if (!canSwitch && companyId) {
          this.router.navigate(corporateCharterRoute(companyId as string), { replaceUrl: true });
          return;
        }
        this.reload$.next();
      });
  }

  protected onSearch(value: string): void {
    const next = (value ?? '').trim();
    if (next === this.search()) {
      return;
    }
    this.search.set(next);
    this.page.set(1);
    this.reload$.next();
  }

  protected onStatusChange(value: StatusFilter | null): void {
    this.status.set(value ?? 'all');
    this.page.set(1);
    this.reload$.next();
  }

  protected clearFilters(): void {
    this.search.set('');
    this.status.set('all');
    this.page.set(1);
    this.reload$.next();
  }

  protected goToPage(page: number): void {
    if (page < 1 || page > this.pages() || page === this.page()) {
      return;
    }
    this.page.set(page);
    this.reload$.next();
  }

  protected retry(): void {
    this.reload$.next();
  }

  protected openCompany(companyId: string): void {
    this.router.navigate(corporateCharterRoute(companyId));
  }

  /** "Enviar contrato social": raiz da empresa no Data Room, onde o upload acontece. */
  protected openDataRoom(companyId: string): void {
    this.router.navigate(['/dashboard/market/data-room/company-folders', companyId], {
      queryParams: { companyId },
    });
  }

  private buildRequest(): CorporateCharterCompaniesRequest {
    const status = this.status();
    return {
      search: this.search() || null,
      status: status === 'all' ? null : status,
      page: this.page(),
      pageSize: this.pageSize,
    };
  }
}
